import { z } from 'zod';
import { boardRoleSchema, grantableBoardRoleSchema } from './board';
import { ulidSchema } from './id';

/**
 * A board's shareable invite link — the contract layer for **B38**, `BE-38.3`.
 *
 * `FR-PERM-019` (generate a link that adds whoever opens it, with no approval
 * step) and `FR-PERM-020` (revoke one, and the several ways a link stops
 * working). Four routes, on `apps/server/src/routes/board-invite-links.ts`:
 *
 * ```
 * GET    /boards/:id/invite-link        → boardInviteLinkSchema | null
 * POST   /boards/:id/invite-link        ← createBoardInviteLinkBodySchema
 * DELETE /boards/:id/invite-link        → 200, bodiless gesture
 * POST   /board-invites/:token/accept   → boardInviteAcceptResultSchema
 * ```
 *
 * `board_invite_links` has existed since `db/postgres/migrations/0002_boards_
 * lists_cards.sql` and has sat unwired ever since. **B38 adds no table and no
 * column**; it wires the one that was already there.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THE TOKEN IS A CREDENTIAL, AND EVERY DECISION IN THIS FILE FOLLOWS FROM
 * THAT ONE FACT
 * ---------------------------------------------------------------------------
 * Anybody holding the token joins the board without approval — that is what
 * `FR-PERM-019` asks for and it is the whole feature. It also means the token
 * is not data about a board; it **is** a membership grant in string form, and
 * three consequences fall out that a reader must not "simplify":
 *
 *   1. **`boardInviteLinkSchema` carries the token, and its GET is behind
 *      `requireBoardMemberManager` rather than `requireBoardReader`.** Serving
 *      it to every reader of a **public** board would hand the internet a
 *      membership grant. `FR-PERM-019` scopes *generation* to "a board member
 *      with invite rights"; reading is the same power, because a reader can
 *      forward. This is the one place in this package where a response's
 *      contents decide its guard rather than the other way round.
 *   2. ⚠⚠ **THE TOKEN NEVER REACHES AN EVENT PAYLOAD, ON EITHER OF B38'S TWO
 *      NEW EVENT NAMES.** `activity_entries` is append-only with no backfill and
 *      `FR-COLLAB-020`'s feed is readable by every board member, so a token
 *      stored there is **a credential that outlives its own revocation** and
 *      that every reader of the feed permanently holds. `board.invite_link_
 *      created` and `board.invite_link_revoked` carry `inviteLinkId` — the row's
 *      primary key — and nothing else that could be redeemed. See `./events`,
 *      where the same warning is written at both names, because the mistake is
 *      made at a write site rather than here.
 *   3. **The redemption route folds four different refusals into one 404 with
 *      one message.** See `boardInviteAcceptResultSchema`.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS DELIBERATELY ABSENT
 * ---------------------------------------------------------------------------
 *   - **`expiresAt`, a TTL, anything time-bounded.** `FR-PERM-020`'s behaviour
 *     text says links do not expire on a timer, and its second edge case names
 *     that as a deliberate divergence from products that do. There is no such
 *     column, none is added, and no scheduled job touches these rows. A field
 *     here would be permanently `null`, which is the dead-on-arrival shape
 *     `boardSchema`'s lifecycle note rejects for `deletedAt`.
 *   - **`isActive`.** The column exists (`0002`) and is how revocation is
 *     expressed, but it can only ever be `true` in a served row: `GET` returns
 *     the board's **active** link or `null`, and a revoked row is not served at
 *     all. A field that is always `true` invites `if (link.isActive)`, a branch
 *     that never fires and that reads as though an inactive link is a thing a
 *     client can be handed.
 *   - **A full URL.** The server states the token; assembling
 *     `<origin>/board-invites/<token>` is the client's, exactly as `./address`
 *     owns the board and card path grammar and no response carries an origin.
 *     There is no configured public origin in this server to build one from.
 *   - **A workspace invite link.** `workspace_invite_links` is a real table and
 *     stays unwired: `FR-PERM-021` is an MVP/B07 requirement and no `BE-38.x`
 *     item names it. B38 owns the **board** link only.
 *   - **An email invitation to somebody with no account.** `board_invitations`
 *     is also real and also unwired — `FR-PERM-018`'s "an invitation is sent and
 *     membership is granted when they create an account" is B10's requirement,
 *     and there is no mail path in this server. `./board`'s
 *     `addBoardMemberBodySchema` records the same refusal from the other side.
 *
 * This file declares no column width constant. `board_invite_links.token` is
 * `varchar(64) COLLATE "C"`, and the token's **length and alphabet are the
 * generator's**, not this file's — the division `./board`'s
 * `SHORT_LINK_MAX_LENGTH` states in full: a width constant here would be the
 * column's ceiling masquerading as the entropy requirement, and
 * `FR-PERM-019`'s unguessability is a property of the generator and its
 * collision arithmetic. The view bounds nothing below because it parses what
 * the column produced.
 */

/**
 * A board's one active invite link, as `GET /boards/:id/invite-link` and
 * `POST /boards/:id/invite-link` both return it.
 *
 * ⚠ **`GET` answers `200` with `null`, not `404`, when no active link exists.**
 * "Is there a link circulating for this board?" is exactly the question an
 * admin is asking, and "no" is an answer rather than an absence — a 404 would
 * be indistinguishable from "no such board", which is the refusal a caller
 * outside the read scope gets and must keep getting.
 *
 * ⚠ **One active link per board at a time** (`FR-PERM-019`). A `POST` while one
 * exists deactivates the old row and creates a new one in a single transaction,
 * emitting **both** `board.invite_link_revoked` and `board.invite_link_created`
 * — the old URL is dead from that moment (`FR-PERM-020` AC4: "given a link is
 * deleted and a new one generated, when the old URL is visited, then it remains
 * dead") and an append-only feed that recorded only the creation would leave a
 * board's history saying two links exist.
 *
 * ⚠ **Revocation is `is_active = false`, never a `DELETE` of the row.** A hard
 * delete would satisfy AC4 only by luck of token randomness; the column exists
 * so that deadness is **stated** rather than inferred, and the surviving row is
 * what `board.invite_link_revoked`'s `inviteLinkId` refers to. It is also why
 * this schema has no `isActive` field — see the header.
 */
export const boardInviteLinkSchema = z.object({
  /**
   * `board_invite_links.id`.
   *
   * On the wire because it is the **only** identifier both B38 event payloads
   * carry (the token is excluded from them by rule — see the header), so a
   * client reacting to `board.invite_link_revoked` on the board socket can tell
   * whether the link it is displaying is the one that just died. Without it the
   * client would have to re-fetch on every permission event on the board.
   */
  id: ulidSchema,
  /**
   * The board this link admits people to.
   *
   * `boardCommentingSchema`'s and `boardMemberPermissionSchema`'s `{ boardId,
   * … }` shape — every narrow per-board companion schema in this package names
   * its board, so a client can key a cache by it without parsing the path it
   * requested.
   */
  boardId: ulidSchema,
  /**
   * `board_invite_links.token varchar(64) COLLATE "C"`, `uq_board_invite_links_
   * token`.
   *
   * ⚠⚠ **This is the credential itself.** It is here because the caller of the
   * routes that serve it is the link's holder-to-be and is already behind
   * `requireBoardMemberManager` — see the header for why reading is the same
   * power as generating. ⚠ **It must never travel anywhere else**: not in an
   * activity payload, not in a socket message, not in a log line, not in an
   * error message. `board.invite_link_created` and `board.invite_link_revoked`
   * carry `inviteLinkId` instead, and `./events` argues why at both names.
   *
   * A bounded string with no format check, on the views' standing rule: the
   * generator owns the alphabet and the length, and a regex here would refuse
   * to serve a row a previous generator legitimately produced.
   *
   * ⚠ **`COLLATE "C"` on the column is load-bearing and invisible from here.**
   * The database collation is `en_US.UTF-8`; a token compared under it would
   * collapse half its alphabet at lookup time and resolve the wrong row — or
   * none — with no error anywhere. `CLAUDE.md` states the rule; this field
   * cannot enforce it and must not be read as doing so.
   */
  token: z.string().min(1),
  /**
   * `board_invite_links.role_granted`, `ck_board_invite_links_role_granted
   * CHECK (role_granted IN ('admin', 'normal', 'observer'))`.
   *
   * ⚠ **All three on the way out, two on the way in** — `boardRoleSchema` here,
   * `grantableBoardRoleSchema` on `createBoardInviteLinkBodySchema` below.
   * `BOARD_VISIBILITIES`' rule exactly: the view admits every value the column
   * admits, the request body admits only the values a route may actually set.
   * `0002` wrote `'admin'` into the CHECK, so a row could hold it — a fixture, a
   * seed, a future block — and a parser narrower than its own CHECK turns a
   * legal row into a 500 on a read. `GRANTABLE_BOARD_ROLES`' docstring in
   * `./board` carries the argument for why **this** block will not create one.
   */
  roleGranted: boardRoleSchema,
  /**
   * `board_invite_links.created_by_user_id`.
   *
   * ⚠ **Not decoration: this link's life depends on this person's standing,
   * evaluated at the moment of redemption.** `FR-PERM-020`: "a link is only
   * honoured while the member who generated it still holds invite rights at the
   * moment of redemption — if the generator is removed from the board or loses
   * invite permission, their outstanding links stop working, **and start
   * working again if they are reinstated**." That last clause is why it cannot
   * be a stored flag and why the redemption path re-evaluates a live predicate.
   * An admin looking at a link that is present but not working has no way to
   * understand it without knowing whose standing it hangs on.
   *
   * A bare id rather than a nested `publicProfileSchema`. The member-list
   * projection is a join this response does not otherwise need, and every
   * caller of these routes is a board member manager who can already read
   * `GET /boards/:id/members`. A block that finds it needs the profile should
   * nest `publicProfileSchema` here on `workspaceJoinRequestSchema`'s
   * precedent — that is a widening, not a change of meaning.
   */
  createdByUserId: ulidSchema,
  /**
   * `board_invite_links.created_at`, serialised as the driver returns it.
   *
   * A bounded string rather than `z.iso.datetime()` or a coerced `Date`, for
   * the reason `workspaceMemberSchema.joinedAt` and `boardMemberSchema.joinedAt`
   * both state: the column is `timestamptz(3)` and `pg` hands it back in
   * Postgres' own spelling (`2026-08-10 12:00:00.123+00`), which is a
   * legitimate instant and not an ISO 8601 `T`/`Z` form.
   *
   * On the wire because **links do not expire** (see the header), so age is the
   * only temporal fact an admin has when deciding whether a circulating link is
   * still one they meant to circulate. `boardSchema` withholds `closedAt` for
   * the opposite reason — no requirement asks when a board was closed — and the
   * contrast is the test to apply before adding a timestamp anywhere else.
   */
  createdAt: z.string().min(1),
});

export type BoardInviteLink = z.infer<typeof boardInviteLinkSchema>;

/**
 * The response of `GET /boards/:id/invite-link` — the link, or `null`.
 *
 * Declared rather than left to the route to spell as `.nullable()`, so that the
 * "no link is not an error" decision is stated once in the contract layer where
 * a reader looking for it will be. See `boardInviteLinkSchema`'s note on why
 * this is a 200 and not a 404.
 */
export const boardInviteLinkOrNullSchema = boardInviteLinkSchema.nullable();

export type BoardInviteLinkOrNull = z.infer<typeof boardInviteLinkOrNullSchema>;

/**
 * `POST /boards/:id/invite-link` — `FR-PERM-019`, "a board member with invite
 * rights can generate a link".
 *
 * ⚠ **`role` is optional and defaults to `'normal'`**, and `'admin'` is a 422 —
 * `GRANTABLE_BOARD_ROLES` in `./board` carries the whole argument, including
 * the form of it that is sharper here than on `POST /boards/:id/members`: an
 * admin-granting link is a URL that hands board administration to whoever
 * forwards it furthest, and `ck_board_invite_links_role_granted` admits
 * `'admin'` at the column, so the refusal has to be stated in the contract
 * rather than assumed from the schema.
 *
 * ⚠ **The same tuple as `addBoardMemberBodySchema.role`, imported rather than
 * restated.** The two bodies grant the same thing by two mechanisms; two
 * declarations would let a later block widen one and not the other, and the
 * one that would be widened by accident is this one, where the consequence is
 * worse.
 *
 * A `strictObject`, so `{ role: 'observer', expiresIn: 3600 }` is a 422 naming
 * the offending key rather than a 201 for a link that will never expire — see
 * the header on why there is no expiry and never will be. An empty body `{}` is
 * valid and yields a `'normal'` link.
 */
export const createBoardInviteLinkBodySchema = z.strictObject({
  role: grantableBoardRoleSchema
    .default('normal')
    .describe(
      'The board role somebody redeeming this link is added at. Optional; ' +
        '“normal” when omitted, which is FR-PERM-018’s stated default. ' +
        '“observer” is the one alternative — an observer reads the board and ' +
        'is refused every write on it (FR-PERM-016). “admin” is refused with ' +
        'a 422: a link is redeemable by anyone holding the URL, so an ' +
        'admin-granting link hands board administration to whoever forwards ' +
        'it furthest.',
    ),
});

export type CreateBoardInviteLinkBody = z.infer<typeof createBoardInviteLinkBodySchema>;

/**
 * What `POST /board-invites/:token/accept` answers with — `FR-PERM-019` AC4.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE ROUTE IS TOP-LEVEL, NOT UNDER `/boards/:id/`, AND THAT IS DELIBERATE
 * ---------------------------------------------------------------------------
 * The redeemer may hold **no relationship whatsoever** to the board and does
 * not know its id. A path under `/boards/:id/` would require them to supply
 * one, and supplying a wrong one would produce a differently-shaped refusal
 * from supplying the right one — which is an oracle, from a URL anybody can
 * hold.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ FOUR REFUSALS COLLAPSE INTO ONE 404 WITH ONE MESSAGE, AND SEPARATING THEM
 * WOULD MAKE THIS ROUTE AN EXISTENCE ORACLE
 * ---------------------------------------------------------------------------
 * Unknown token; token whose row is `is_active = false`; token whose **creator**
 * no longer holds invite rights on the board (`FR-PERM-020`'s implicit
 * revocation, evaluated live); board soft-deleted. **All four → `404 "This
 * invite link is no longer valid"`.** Distinguishing them would let a caller
 * learn from the *shape* of a refusal that a board exists, that a particular
 * link once existed, and that a particular person was removed from that board —
 * three facts about a board they cannot see. This is `boardAccessOf`'s
 * three-states-one-404 rule applied to a surface reachable by anybody holding a
 * URL.
 *
 * Then, and only then, the ordinary ladder: **404, then 403, then 409.** A
 * closed board is a `409` after the token has resolved, because a holder of a
 * valid live token is entitled to a real answer and `assertBoardIsOpen` is this
 * codebase's standing refusal for "the board's state forbids this".
 *
 * ⚠ **`requireUser`, so a signed-out caller gets 401.** `FR-PERM-019` AC3
 * ("a signed-out visitor … when they sign in or sign up, then they are added")
 * is `FE-38.2`'s redirect, not a server behaviour: the server has no way to
 * hold an intent across a sign-up. **This route adds no anonymous-capable
 * surface.**
 *
 * ---------------------------------------------------------------------------
 * WHY `200` AND NOT `201`, ON BOTH PATHS
 * ---------------------------------------------------------------------------
 * The route brings a *membership* into existence, not the resource its path
 * addresses — and, more decisively, the already-a-member path must be
 * **indistinguishable in shape** from the first-time path (`FR-PERM-019` AC4).
 * A `201` on one and a `200` on the other would put the difference in the
 * status code, where a client is most likely to branch on it and least likely
 * to have been told that both are successes.
 */
export const boardInviteAcceptResultSchema = z.object({
  /**
   * The board the caller is now on.
   *
   * The redeemer arrived holding a token and nothing else, so this is how they
   * learn where they have landed — without it the client's only next move would
   * be to guess. It is the id rather than the whole `boardSchema` because the
   * client's next request is `GET /boards/:id` or `GET /boards/:id/contents`
   * anyway, and nesting a board here would make this response the second place
   * a board's title and visibility are served on a path whose guard is about a
   * token rather than about the board.
   */
  boardId: ulidSchema,
  /**
   * The role the caller **now holds on this board**.
   *
   * ⚠⚠ **On the already-a-member path this is their EXISTING role, never the
   * link's `role_granted`.** `FR-PERM-019` AC4 makes redemption by an existing
   * member a no-op — no row written, no event emitted, the role unchanged — and
   * returning the link's role instead would tell a normal member who redeemed
   * an observer link that they had just been demoted, and a board admin that
   * they had been reduced to a member. Neither would be true, and the response
   * is the only thing the client has to render from.
   *
   * `boardRoleSchema`, all three values, for that reason: the caller's existing
   * role may be `'admin'` even though no link can grant one.
   */
  role: boardRoleSchema,
  /**
   * Whether the caller was already on this board before redeeming.
   *
   * `FR-PERM-019` AC4's no-op made observable — `addBoardMemberResultSchema`'s
   * `added` and `addWorkspaceMemberResultSchema`'s, inverted in sense and named
   * for the state rather than for the act because that is what the redeemer's
   * client renders ("you're already on this board" versus "welcome"). Both
   * outcomes are 200s, so the status code cannot carry the difference; this
   * field is the only thing that can.
   *
   * ⚠ **`true` means nothing was written and nothing was emitted.** No
   * `board_members` row, no `board.member_added`, no timestamp moved. A test
   * that only asserts the status code cannot tell the two paths apart, which is
   * exactly why this field exists.
   */
  alreadyMember: z.boolean(),
});

export type BoardInviteAcceptResult = z.infer<typeof boardInviteAcceptResultSchema>;
